/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Heart,
  MapPin,
  Users,
  Calendar,
  Sparkles,
  CheckCircle,
  Phone,
  Star,
  Crown,
  Gem,
  Music,
  Camera,
  Utensils,
  Plane,
  Hotel,
  ClipboardCheck,
  Gift,
  PartyPopper,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeddingzPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    partner_name: "",
    destination: "",
    wedding_date: "",
    guest_count: "",
    budget: "",
    wedding_style: "",
    events_planned: "",
    accommodation_needed: "",
    special_requirements: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/wedding-enquiry`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        partner_name: formData.partner_name,
        destination: formData.destination,
        wedding_date: formData.wedding_date,
        guest_count: parseInt(formData.guest_count) || null,
        budget: formData.budget,
        wedding_style: formData.wedding_style,
        events_planned: formData.events_planned,
        accommodation_needed: formData.accommodation_needed,
        special_requirements: formData.special_requirements,
      });
      toast.success("Thank you! Our wedding specialists will contact you within 24 hours.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        partner_name: "",
        destination: "",
        wedding_date: "",
        guest_count: "",
        budget: "",
        wedding_style: "",
        events_planned: "",
        accommodation_needed: "",
        special_requirements: "",
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const services = [
    { icon: MapPin, title: "Destination Wedding Planning", desc: "Complete planning for dream weddings in India & International destinations" },
    { icon: Hotel, title: "Venue Sourcing & Negotiations", desc: "Access to premium venues with best rates and exclusive packages" },
    { icon: Camera, title: "Vendor Coordination", desc: "Curated network of photographers, decorators, makeup artists & entertainers" },
    { icon: Calendar, title: "Timeline & Logistics", desc: "Detailed planning ensuring seamless execution of every wedding event" },
    { icon: Plane, title: "Guest Travel & Hospitality", desc: "End-to-end travel arrangements and hospitality for all wedding guests" },
    { icon: Sparkles, title: "Wedding Design & Decor", desc: "Bespoke themes and décor that reflect your unique love story" },
  ];

  const guestServices = [
    { icon: ClipboardCheck, title: "RSVP Management", desc: "Digital invitations & response tracking" },
    { icon: Users, title: "Guest Confirmations", desc: "Systematic follow-ups & attendance tracking" },
    { icon: Plane, title: "Travel Bookings", desc: "Flights & transportation for all guests" },
    { icon: Hotel, title: "Hotel Allocations", desc: "Room blocking & rooming list management" },
    { icon: MapPin, title: "Airport Transfers", desc: "Seamless pickup & drop coordination" },
    { icon: Phone, title: "On-ground Support", desc: "Dedicated team for guest assistance" },
  ];

  const eventTypes = [
    { icon: Sparkles, title: "Mehendi Ceremony", desc: "Traditional celebrations with modern elegance" },
    { icon: Music, title: "Sangeet Night", desc: "Unforgettable musical celebrations" },
    { icon: Gem, title: "Cocktail & Reception", desc: "Sophisticated evening celebrations" },
    { icon: Crown, title: "Wedding Ceremony", desc: "Your perfect wedding day, flawlessly executed" },
    { icon: Utensils, title: "Post-Wedding Brunch", desc: "Memorable farewell gatherings" },
    { icon: Star, title: "Artist Coordination", desc: "Top performers & entertainment" },
  ];

  const domesticDestinations = [
    { name: "Udaipur", image: "https://images.unsplash.com/photo-1585128792020-803d29415281?w=600", tagline: "City of Lakes" },
    { name: "Goa", image: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=600", tagline: "Beach Paradise" },
    { name: "Jaipur", image: "https://images.unsplash.com/photo-1477587458883-47145ed94245?w=600", tagline: "Royal Heritage" },
    { name: "Kerala", image: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=600", tagline: "God's Own Country" },
  ];

  const internationalDestinations = [
    { name: "Thailand", image: "https://images.unsplash.com/photo-1528181304800-259b08848526?w=600", tagline: "Tropical Romance" },
    { name: "Bali", image: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=600", tagline: "Island Paradise" },
    { name: "Dubai", image: "https://images.unsplash.com/photo-1651063820152-d3e7a27b4d2b?w=600", tagline: "Luxury Redefined" },
    { name: "Europe", image: "https://images.unsplash.com/photo-1527668752968-14dc70a27c95?w=600", tagline: "Timeless Elegance" },
  ];

  const whyChooseUs = [
    { icon: CheckCircle, title: "One-Point Coordination", desc: "Single dedicated planner for complete peace of mind" },
    { icon: Gem, title: "Transparent Costing", desc: "Clear pricing with no hidden charges or surprises" },
    { icon: Crown, title: "Dedicated Wedding Planners", desc: "Experienced specialists who understand your vision" },
    { icon: MapPin, title: "Destination Expertise", desc: "8+ years of DMC experience across destinations" },
    { icon: Heart, title: "Guest-First Approach", desc: "Every guest treated like family, every detail matters" },
  ];

  return (
    <div data-testid="weddingz-page" className="bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="weddingz-hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1920&q=80"
            alt="Luxury Wedding"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent" />
        </div>

        <div className="container-custom relative z-10 pt-24">
          <motion.div
            className="max-w-2xl"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Heart className="h-6 w-6 text-rose-400" />
              <span className="text-rose-300 font-medium tracking-wider uppercase text-sm">
                Weddingz by DNO
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6">
              Crafting Timeless Destination Weddings &{" "}
              <span className="text-rose-300">Wedding Experiences</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed">
              From intimate ceremonies to grand celebrations, we transform your dream wedding into 
              an unforgettable reality. Full-service wedding planning with exceptional guest management.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#consultation-form">
                <Button className="bg-rose-500 hover:bg-rose-600 text-white h-14 px-8 rounded-full font-medium text-base shadow-lg" data-testid="hero-plan-wedding-btn">
                  <Heart className="mr-2 h-5 w-5" />
                  Plan Your Wedding
                </Button>
              </a>
              <a href="#consultation-form">
                <Button className="bg-white/10 backdrop-blur-sm border-2 border-white/50 text-white hover:bg-white hover:text-slate-900 h-14 px-8 rounded-full font-medium text-base" data-testid="hero-consultation-btn">
                  Request a Wedding Consultation
                </Button>
              </a>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10" />
      </section>

      {/* About Weddingz by DNO - NO STATS */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-weddingz">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp}>
              <span className="inline-flex items-center gap-2 text-rose-500 font-medium tracking-wider uppercase text-sm mb-4">
                <Heart className="h-4 w-4" />
                About Us
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-slate-800 mb-6">
                Where Dreams Meet <span className="text-rose-500">Perfection</span>
              </h2>
              <div className="space-y-4 text-slate-600 text-lg leading-relaxed">
                <p>
                  <strong className="text-slate-800">Weddingz by DNO</strong> is the specialized wedding 
                  & destination events vertical of DNO Events & DMC Private Limited. With over 8 years 
                  of experience in destination management, we bring unparalleled expertise to crafting 
                  your perfect wedding.
                </p>
                <p>
                  Our approach combines <strong className="text-slate-800">precision planning</strong>, 
                  <strong className="text-slate-800"> seamless execution</strong>, and 
                  <strong className="text-slate-800"> exceptional guest management</strong> — ensuring 
                  every moment of your celebration is nothing short of magical.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=800"
                alt="Elegant Wedding Setup"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-rose-50 p-6 rounded-2xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-rose-500 rounded-full flex items-center justify-center">
                    <Crown className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800">Premium Service</div>
                    <div className="text-sm text-slate-500">White-glove experience</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FREE HONEYMOON OFFER - Creative Section */}
      <section className="py-16 bg-gradient-to-r from-rose-500 via-pink-500 to-rose-600 relative overflow-hidden" data-testid="honeymoon-offer">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6">
              <Gift className="h-5 w-5 text-white" />
              <span className="text-white font-medium">Exclusive Offer</span>
              <PartyPopper className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
              Your Love Story Deserves a <span className="text-yellow-300">Bonus Chapter</span>
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8 leading-relaxed">
              Book your destination wedding with us and receive a <strong className="text-yellow-300">Surprise Honeymoon Package</strong> — 
              completely on us! Because every fairytale wedding deserves an equally magical beginning to forever.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl px-6 py-4">
                <Plane className="h-8 w-8 text-white mx-auto mb-2" />
                <div className="text-white font-semibold">Flight Included</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl px-6 py-4">
                <Hotel className="h-8 w-8 text-white mx-auto mb-2" />
                <div className="text-white font-semibold">Luxury Stay</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl px-6 py-4">
                <Heart className="h-8 w-8 text-white mx-auto mb-2" />
                <div className="text-white font-semibold">Romantic Experiences</div>
              </div>
            </div>
            <p className="text-white/70 text-sm mt-6">*Terms & conditions apply. Valid for weddings booked with 100+ guests.</p>
          </motion.div>
        </div>
      </section>

      {/* Destination Wedding Services */}
      <section className="py-20 md:py-28 bg-rose-50/50" data-testid="wedding-services">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 text-rose-500 font-medium tracking-wider uppercase text-sm mb-4">
              <Sparkles className="h-4 w-4" />
              Our Services
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-slate-800 mb-4">
              Destination Wedding Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive wedding planning that covers every detail, from venue selection to vendor coordination
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 group" data-testid={`wedding-service-${index}`}>
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-rose-500 transition-colors">
                      <service.icon className="h-7 w-7 text-rose-500 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-slate-800 mb-3">{service.title}</h3>
                    <p className="text-slate-600">{service.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Guest Management Section */}
      <section className="py-20 md:py-28 bg-slate-900 relative overflow-hidden" data-testid="guest-management">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 text-rose-400 font-medium tracking-wider uppercase text-sm mb-4">
              <Users className="h-4 w-4" />
              Our Specialty
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
              Wedding RSVP & Guest Management
            </h2>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              What sets us apart — comprehensive guest management that ensures every attendee 
              has a seamless, memorable experience from arrival to departure
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guestServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all" data-testid={`guest-service-${index}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-rose-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <service.icon className="h-6 w-6 text-rose-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                      <p className="text-white/60 text-sm">{service.desc}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeInUp} className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-rose-500/20 border border-rose-500/30 rounded-full px-6 py-3">
              <CheckCircle className="h-5 w-5 text-rose-400" />
              <span className="text-white font-medium">
                Your guests are our responsibility — treated like family
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Wedding Event Management */}
      <section className="py-20 md:py-28 bg-white" data-testid="event-management">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 text-rose-500 font-medium tracking-wider uppercase text-sm mb-4">
              <Music className="h-4 w-4" />
              Event Management
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-slate-800 mb-4">
              Every Celebration, Flawlessly Executed
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From pre-wedding festivities to post-wedding brunches, we manage every event with precision
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {eventTypes.map((event, index) => (
              <motion.div
                key={event.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-center p-8 rounded-2xl bg-gradient-to-b from-rose-50 to-white border border-rose-100 hover:shadow-lg transition-all" data-testid={`event-type-${index}`}>
                  <div className="w-16 h-16 mx-auto bg-rose-100 rounded-full flex items-center justify-center mb-6">
                    <event.icon className="h-8 w-8 text-rose-500" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-slate-800 mb-2">{event.title}</h3>
                  <p className="text-slate-600">{event.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Destination Options */}
      <section className="py-20 md:py-28 bg-rose-50/50" data-testid="destinations">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 text-rose-500 font-medium tracking-wider uppercase text-sm mb-4">
              <MapPin className="h-4 w-4" />
              Destinations
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-slate-800 mb-4">
              Your Dream Wedding Location Awaits
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Whether you dream of a palace wedding in India or an exotic beach celebration abroad, we make it happen
            </p>
          </motion.div>

          {/* Domestic */}
          <div className="mb-16">
            <motion.h3 {...fadeInUp} className="text-2xl font-heading font-semibold text-slate-800 mb-8 text-center">
              Domestic Destinations
            </motion.h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {domesticDestinations.map((dest, index) => (
                <motion.div
                  key={dest.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="group relative aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer" data-testid={`domestic-dest-${index}`}>
                    <img src={dest.image} alt={dest.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h4 className="text-2xl font-heading font-bold text-white mb-1">{dest.name}</h4>
                      <p className="text-white/80 text-sm">{dest.tagline}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* International */}
          <div>
            <motion.h3 {...fadeInUp} className="text-2xl font-heading font-semibold text-slate-800 mb-8 text-center">
              International Destinations
            </motion.h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {internationalDestinations.map((dest, index) => (
                <motion.div
                  key={dest.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="group relative aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer" data-testid={`intl-dest-${index}`}>
                    <img src={dest.image} alt={dest.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h4 className="text-2xl font-heading font-bold text-white mb-1">{dest.name}</h4>
                      <p className="text-white/80 text-sm">{dest.tagline}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div {...fadeInUp} className="text-center mt-12">
            <p className="text-slate-600 mb-4">Don't see your dream destination? We customize weddings at any location worldwide.</p>
            <a href="#consultation-form">
              <Button className="bg-rose-500 hover:bg-rose-600 text-white h-12 px-8 rounded-full">
                Discuss Your Destination
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Why Weddingz by DNO */}
      <section className="py-20 md:py-28 bg-white" data-testid="why-choose">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <img src="https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=800" alt="Wedding Planning" className="rounded-2xl shadow-2xl" />
            </motion.div>
            <motion.div {...fadeInUp}>
              <span className="inline-flex items-center gap-2 text-rose-500 font-medium tracking-wider uppercase text-sm mb-4">
                <Crown className="h-4 w-4" />
                Why Choose Us
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-slate-800 mb-8">
                Why Weddingz by DNO?
              </h2>
              <div className="space-y-6">
                {whyChooseUs.map((item, index) => (
                  <div key={item.title} className="flex gap-4" data-testid={`why-item-${index}`}>
                    <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-6 w-6 text-rose-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">{item.title}</h4>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Wedding Consultation Form */}
      <section id="consultation-form" className="py-20 md:py-28 bg-gradient-to-b from-rose-50 to-rose-100/50" data-testid="consultation-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div {...fadeInUp}>
              <span className="inline-flex items-center gap-2 text-rose-500 font-medium tracking-wider uppercase text-sm mb-4">
                <Heart className="h-4 w-4" />
                Begin Your Journey
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-slate-800 mb-6">
                Request a Wedding <span className="text-rose-500">Consultation</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Share your vision with us, and our dedicated wedding specialists will craft 
                a personalized proposal for your special day.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  "Free consultation with no obligations",
                  "Response within 24 hours",
                  "Personalized wedding proposal",
                  "Transparent pricing from the start",
                  "Complimentary honeymoon package for eligible weddings",
                ].map((item, index) => (
                  <div key={`benefit-${item.slice(0, 20)}`} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-rose-500 flex-shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </div>
                ))}
              </div>

              {/* Contact Info */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-rose-100">
                <h4 className="font-heading font-semibold text-slate-800 mb-4">Prefer to talk?</h4>
                <div className="space-y-3">
                  <a href="tel:+918369033791" className="flex items-center gap-3 text-slate-600 hover:text-rose-500 transition-colors">
                    <Phone className="h-5 w-5 text-rose-500" />
                    +91 8369033791
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Consultation Form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card className="shadow-2xl border-0" data-testid="wedding-form-card">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-heading font-semibold text-slate-800 mb-6">
                    Wedding Consultation Form
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Your Name *</Label>
                        <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="h-12" placeholder="Your full name" data-testid="wedding-input-name" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="partner_name">Partner's Name</Label>
                        <Input id="partner_name" name="partner_name" value={formData.partner_name} onChange={handleChange} className="h-12" placeholder="Partner's name" data-testid="wedding-input-partner" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="h-12" placeholder="your@email.com" data-testid="wedding-input-email" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="h-12" placeholder="+91 98765 43210" data-testid="wedding-input-phone" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Preferred Destination</Label>
                        <Select value={formData.destination} onValueChange={(value) => setFormData({ ...formData, destination: value })}>
                          <SelectTrigger className="h-12" data-testid="wedding-select-destination">
                            <SelectValue placeholder="Select destination" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="udaipur">Udaipur, Rajasthan</SelectItem>
                            <SelectItem value="goa">Goa</SelectItem>
                            <SelectItem value="jaipur">Jaipur, Rajasthan</SelectItem>
                            <SelectItem value="kerala">Kerala</SelectItem>
                            <SelectItem value="thailand">Thailand</SelectItem>
                            <SelectItem value="bali">Bali, Indonesia</SelectItem>
                            <SelectItem value="dubai">Dubai, UAE</SelectItem>
                            <SelectItem value="europe">Europe</SelectItem>
                            <SelectItem value="other">Other / Not Sure</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="wedding_date">Wedding Date / Month *</Label>
                        <Input id="wedding_date" name="wedding_date" value={formData.wedding_date} onChange={handleChange} required className="h-12" placeholder="e.g., December 2025" data-testid="wedding-input-date" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="guest_count">Expected Number of Guests *</Label>
                        <Input id="guest_count" name="guest_count" type="number" min="1" value={formData.guest_count} onChange={handleChange} required className="h-12" placeholder="e.g., 150" data-testid="wedding-input-guests" />
                      </div>
                      <div className="space-y-2">
                        <Label>Estimated Budget Range</Label>
                        <Select value={formData.budget} onValueChange={(value) => setFormData({ ...formData, budget: value })}>
                          <SelectTrigger className="h-12" data-testid="wedding-select-budget">
                            <SelectValue placeholder="Select budget" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="25-50L">₹25 Lakhs - ₹50 Lakhs</SelectItem>
                            <SelectItem value="50-75L">₹50 Lakhs - ₹75 Lakhs</SelectItem>
                            <SelectItem value="75L-1Cr">₹75 Lakhs - ₹1 Crore</SelectItem>
                            <SelectItem value="1Cr-2Cr">₹1 Crore - ₹2 Crores</SelectItem>
                            <SelectItem value="2Cr+">Above ₹2 Crores</SelectItem>
                            <SelectItem value="flexible">Flexible / Discuss</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Wedding Style Preference</Label>
                      <Select value={formData.wedding_style} onValueChange={(value) => setFormData({ ...formData, wedding_style: value })}>
                        <SelectTrigger className="h-12" data-testid="wedding-select-style">
                          <SelectValue placeholder="Select style" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="traditional">Traditional Indian</SelectItem>
                          <SelectItem value="modern">Modern & Contemporary</SelectItem>
                          <SelectItem value="fusion">Fusion (Traditional + Modern)</SelectItem>
                          <SelectItem value="destination">Destination Beach/Resort</SelectItem>
                          <SelectItem value="royal">Royal/Palace Theme</SelectItem>
                          <SelectItem value="intimate">Intimate & Minimalist</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Events You're Planning</Label>
                      <Select value={formData.events_planned} onValueChange={(value) => setFormData({ ...formData, events_planned: value })}>
                        <SelectTrigger className="h-12" data-testid="wedding-select-events">
                          <SelectValue placeholder="Select events" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Events (Mehendi, Sangeet, Wedding, Reception)</SelectItem>
                          <SelectItem value="wedding-reception">Wedding & Reception Only</SelectItem>
                          <SelectItem value="full-week">Full Week Celebration</SelectItem>
                          <SelectItem value="custom">Custom / Will Discuss</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Guest Accommodation Required?</Label>
                      <Select value={formData.accommodation_needed} onValueChange={(value) => setFormData({ ...formData, accommodation_needed: value })}>
                        <SelectTrigger className="h-12" data-testid="wedding-select-accommodation">
                          <SelectValue placeholder="Select option" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes-all">Yes, for all guests</SelectItem>
                          <SelectItem value="yes-some">Yes, for some guests</SelectItem>
                          <SelectItem value="no">No, guests will arrange themselves</SelectItem>
                          <SelectItem value="discuss">Need to discuss</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="special_requirements">Special Requirements or Vision</Label>
                      <Textarea
                        id="special_requirements"
                        name="special_requirements"
                        value={formData.special_requirements}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Tell us about your dream wedding, any specific themes, dietary requirements, accessibility needs, or questions you have..."
                        data-testid="wedding-textarea-requirements"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-rose-500 hover:bg-rose-600 text-white h-14 rounded-full text-base font-medium"
                      disabled={isSubmitting}
                      data-testid="wedding-submit-btn"
                    >
                      {isSubmitting ? "Submitting..." : "Request Consultation"}
                      <Heart className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brand Footer */}
      <section className="py-12 bg-slate-900" data-testid="brand-footer">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Heart className="h-6 w-6 text-rose-400" />
              <span className="text-2xl font-heading font-bold text-white">Weddingz by DNO</span>
            </div>
            <p className="text-white/60 max-w-2xl mx-auto">
              Weddingz by DNO is a specialized wedding & destination events vertical of{" "}
              <span className="text-white font-medium">DNO Events & DMC Private Limited</span>.
              Bringing 8+ years of destination management expertise to craft your perfect wedding.
            </p>
            <div className="mt-6 flex items-center justify-center gap-4">
              <a href="tel:+918369033791" className="text-white/60 hover:text-rose-400 transition-colors text-sm">
                +91 8369033791
              </a>
              <span className="text-white/30">|</span>
              <a href="mailto:info@dnotours.com" className="text-white/60 hover:text-rose-400 transition-colors text-sm">
                info@dnotours.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WeddingzPage;
